/**
 * ValutaGenio · /perche-vg
 *
 * Mono-target Property Advisor. Nasce come pagina che un PA può condividere
 * con un collega per spiegargli il concetto ValutaGenio prima ancora di
 * parlare di iscrizione. Riusa `PerchePASection` e `ComeFunzionaSection`
 * (le stesse sezioni che vivono sopra il signup PA e sulla pagina Codice di
 * Condotta), senza duplicare copy. Chiude con un unico CTA verso /signup.
 *
 * Nessun contenuto rivolto ai proprietari. Nessuna doppia CTA.
 */
import React from "react";
import { ArrowRight } from "lucide-react";
import Logo from "@/components/landing/Logo";
import Footer from "@/components/landing/Footer";
import {
  PerchePASection,
  ComeFunzionaSection,
} from "@/pages/coc/CoCSections";

// ---------- SEO metadata (no react-helmet dependency, mirrors PA landing) ---

function usePercheSEO() {
  React.useEffect(() => {
    const prevTitle = document.title;
    document.title =
      "Perché ValutaGenio · Un caso già strutturato prima della call";
    const metas = [
      {
        name: "description",
        content:
          "Perché ValutaGenio esiste e come cambia il primo contatto con il proprietario per chi lavora nel settore immobiliare: un caso già strutturato, un piano d'azione condiviso, un percorso trasparente.",
      },
      {
        property: "og:title",
        content: "Perché ValutaGenio — Un caso già strutturato prima della call",
      },
      {
        property: "og:description",
        content:
          "Il proprietario ti arriva con un'analisi già fatta e un orientamento già chiaro. Tu ti concentri su ciò che serve per far avanzare il caso.",
      },
      { property: "og:type", content: "website" },
    ];
    const attached = [];
    metas.forEach((m) => {
      const key = m.name ? "name" : "property";
      const val = m.name || m.property;
      let el = document.querySelector(`meta[${key}="${val}"]`);
      let created = false;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(key, val);
        document.head.appendChild(el);
        created = true;
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", m.content);
      attached.push({ el, prev, created });
    });
    let canonical = document.querySelector('link[rel="canonical"]');
    let canonicalCreated = false;
    const prevCanonical = canonical ? canonical.getAttribute("href") : null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
      canonicalCreated = true;
    }
    canonical.setAttribute("href", "/perche-vg");
    return () => {
      document.title = prevTitle;
      attached.forEach(({ el, prev, created }) => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute("content", prev);
      });
      if (canonicalCreated) canonical.remove();
      else if (prevCanonical !== null)
        canonical.setAttribute("href", prevCanonical);
    };
  }, []);
}

// ---------- ref preservation on signup CTA --------------------------------

function useRefParam() {
  return React.useMemo(() => {
    try {
      const u = new URL(window.location.href);
      const ref = u.searchParams.get("ref");
      return ref && ref.trim() ? ref.trim() : null;
    } catch {
      return null;
    }
  }, []);
}

function signupHref(ref) {
  return ref ? `/signup?ref=${encodeURIComponent(ref)}` : "/signup";
}

// ---------- navbar (bounded, no dashboard links) --------------------------

function PercheNavbar() {
  return (
    <header
      data-testid="perche-vg-navbar"
      className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-sm border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        <a
          href="/"
          data-testid="perche-vg-logo-home"
          className="flex items-center"
        >
          <Logo size="text-xl md:text-2xl" />
        </a>
        <nav className="flex items-center gap-3 md:gap-6 text-sm">
          <a
            href="/property-advisor"
            data-testid="perche-vg-nav-pa-landing"
            className="hidden md:inline text-[#0A0A0A]/70 hover:text-[#B40101] transition-colors"
          >
            Per Property Advisor
          </a>
          <a
            href="#iscriviti"
            data-testid="perche-vg-nav-cta"
            className="inline-flex items-center gap-2 bg-[#B40101] hover:bg-[#8F0101] text-white font-medium text-sm px-4 py-2 rounded-lg transition-colors"
          >
            Iscriviti
          </a>
        </nav>
      </div>
    </header>
  );
}

// ---------- hero -----------------------------------------------------------

function Hero() {
  return (
    <section
      data-testid="perche-vg-hero"
      className="relative bg-[#F9F9F9] border-b border-[#E5E7EB]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-16 md:pb-20">
        <span className="inline-block text-[11px] uppercase tracking-[0.22em] font-semibold text-[#B40101] mb-4">
          Perché ValutaGenio
        </span>
        <h1
          data-testid="perche-vg-hero-h1"
          className="font-serif text-[#0A0A0A] text-4xl sm:text-5xl lg:text-[56px] leading-[1.08] tracking-tight max-w-3xl"
        >
          Un caso già strutturato prima della call. Ecco perché.
        </h1>
        <p className="mt-7 text-[#0A0A0A]/80 text-[17px] md:text-[18px] leading-relaxed max-w-2xl">
          Una pagina pensata per chi lavora nel settore immobiliare e vuole
          capire, in pochi minuti, cosa fa davvero ValutaGenio per un Property
          Advisor — e per il proprietario che gli arriva davanti.
        </p>
      </div>
    </section>
  );
}

// ---------- content wrapper (padded reuse of CoCSections) -----------------

function ReuseWrapper({ children, testid, bg }) {
  return (
    <section
      data-testid={testid}
      className={`${bg} border-b border-[#E5E7EB]`}
    >
      <div className="max-w-[900px] mx-auto px-5 md:px-8 py-16 md:py-20">
        {children}
      </div>
    </section>
  );
}

// ---------- final CTA -----------------------------------------------------

function FinalCTA() {
  const ref = useRefParam();
  return (
    <section
      id="iscriviti"
      data-testid="perche-vg-final-cta"
      className="bg-white"
    >
      <div className="max-w-[900px] mx-auto px-5 md:px-8 py-16 md:py-24 text-center">
        <span className="inline-block text-[11px] uppercase tracking-[0.22em] font-semibold text-[#B40101] mb-4">
          Sei pronto?
        </span>
        <h2 className="font-serif text-[#0A0A0A] text-3xl md:text-4xl lg:text-[42px] leading-[1.15] tracking-tight">
          Entra in ValutaGenio come Property Advisor.
        </h2>
        <p className="mt-6 text-[#0A0A0A]/80 text-[17px] leading-relaxed max-w-2xl mx-auto">
          Ti bastano pochi minuti per completare l&apos;iscrizione. Dopo aver
          accettato il Codice di Condotta, la tua scheda è pronta a ricevere
          casi già strutturati.
        </p>
        <div className="mt-10 flex justify-center">
          <a
            href={signupHref(ref)}
            data-testid="perche-vg-signup-cta"
            className="inline-flex items-center gap-2 bg-[#B40101] hover:bg-[#8F0101] text-white font-semibold text-base px-6 py-3.5 rounded-xl transition-all duration-200 hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(180,1,1,0.28)]"
            aria-label="Iscriviti come Property Advisor"
          >
            Iscriviti come Property Advisor
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
        {ref && (
          <p
            data-testid="perche-vg-ref-hint"
            className="mt-4 text-[13px] text-[#0A0A0A]/70"
          >
            Sei stato invitato con il codice{" "}
            <span className="font-semibold text-[#B40101]">{ref}</span>. Il
            codice viene preservato all&apos;iscrizione.
          </p>
        )}
      </div>
    </section>
  );
}

// ---------- page ----------------------------------------------------------

export default function PercheVG() {
  usePercheSEO();
  return (
    <main
      data-testid="perche-vg-page"
      className="bg-white text-[#0A0A0A] antialiased overflow-x-hidden"
    >
      <PercheNavbar />
      <Hero />
      <ReuseWrapper testid="perche-vg-perche-section" bg="bg-white">
        <PerchePASection />
      </ReuseWrapper>
      <ReuseWrapper testid="perche-vg-come-section" bg="bg-[#F9F9F9]">
        <ComeFunzionaSection />
      </ReuseWrapper>
      <FinalCTA />
      <Footer />
    </main>
  );
}
